import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Loader2, ArrowRight, Check, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const SOURCE_LABELS = {
  customer: 'Customer',
  meta_lead: 'Meta Lead',
  wix_lead: 'Website Lead',
  email_lead: 'Email Lead',
};

const SOURCE_COLORS = {
  customer: 'bg-blue-100 text-blue-700 border-blue-200',
  meta_lead: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  wix_lead: 'bg-teal-100 text-teal-700 border-teal-200',
  email_lead: 'bg-amber-100 text-amber-700 border-amber-200',
};

const FIELD_LABELS = {
  first_name: 'First Name',
  last_name: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  postcode: 'Postcode',
  city: 'City',
  county: 'County',
  message: 'Message',
  company: 'Company',
};

export const MergeDuplicatesDialog = ({ open, onClose, sourceType, sourceId, targetType, targetId, onMerged }) => {
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selections, setSelections] = useState({});

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get('/duplicates/preview', {
      params: { source_type: sourceType, source_id: sourceId, target_type: targetType, target_id: targetId }
    }).then(res => {
      setPreview(res.data);
      // Auto-select: prefer non-empty values, default to target
      const auto = {};
      for (const field of res.data.fields) {
        const sv = res.data.source[field] || '';
        const tv = res.data.target[field] || '';
        if (tv) auto[field] = 'target';
        else if (sv) auto[field] = 'source';
        else auto[field] = 'target';
      }
      setSelections(auto);
    }).catch(() => {
      toast.error('Failed to load merge preview');
      onClose();
    }).finally(() => setLoading(false));
  }, [open, sourceType, sourceId, targetType, targetId]);

  const handleMerge = async () => {
    setMerging(true);
    try {
      const res = await api.post('/duplicates/merge', {
        source_type: sourceType,
        source_id: sourceId,
        target_type: targetType,
        target_id: targetId,
        field_selections: selections,
        delete_source: true,
      });
      toast.success(res.data.message);
      onMerged?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(false);
    }
  };

  const toggle = (field) => {
    setSelections(prev => ({
      ...prev,
      [field]: prev[field] === 'source' ? 'target' : 'source',
    }));
  };

  const hasAnyDifference = preview?.fields.some(f => {
    const sv = preview.source[f] || '';
    const tv = preview.target[f] || '';
    return sv !== tv && (sv || tv);
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="merge-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="w-5 h-5 text-blue-600" />
            Merge Duplicates
          </DialogTitle>
          <DialogDescription className="sr-only">Compare and merge duplicate records</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : preview ? (
          <div className="space-y-4">
            {/* Header labels — read effective types from preview (server may swap
                source/target so the customer is always the master target). */}
            <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center">
              <div className="text-center">
                <Badge className={SOURCE_COLORS[preview.source.source_type] || SOURCE_COLORS[sourceType]} data-testid="merge-source-label">
                  {SOURCE_LABELS[preview.source.source_type] || SOURCE_LABELS[sourceType]}
                </Badge>
                <p className="text-xs text-slate-500 mt-1">
                  {preview.source.first_name} {preview.source.last_name}
                </p>
                <p className="text-[10px] text-slate-400 mt-0.5">will be deleted</p>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
              <div className="text-center">
                <Badge className={SOURCE_COLORS[preview.target.source_type] || SOURCE_COLORS[targetType]} data-testid="merge-target-label">
                  {SOURCE_LABELS[preview.target.source_type] || SOURCE_LABELS[targetType]}
                </Badge>
                <p className="text-xs text-slate-500 mt-1">
                  {preview.target.first_name} {preview.target.last_name}
                </p>
                <p className="text-[10px] text-emerald-600 font-medium mt-0.5">master record (kept)</p>
              </div>
            </div>

            <p className="text-xs text-slate-500 text-center">
              Click a value to select it. The selected value (highlighted) will be kept.
            </p>

            {/* Field comparison rows */}
            <div className="space-y-1 border rounded-lg overflow-hidden">
              {preview.fields.map(field => {
                const sv = preview.source[field] || '';
                const tv = preview.target[field] || '';
                if (!sv && !tv) return null;
                const sel = selections[field] || 'target';
                const isDiff = sv !== tv;

                return (
                  <div key={field} className="grid grid-cols-[1fr_auto_1fr] gap-0 items-stretch border-b last:border-b-0">
                    {/* Source value */}
                    <button
                      onClick={() => isDiff && toggle(field)}
                      disabled={!isDiff}
                      className={`text-left px-3 py-2.5 text-sm transition-colors ${
                        sel === 'source'
                          ? 'bg-blue-50 border-l-2 border-l-blue-500'
                          : 'hover:bg-slate-50 border-l-2 border-l-transparent'
                      } ${!isDiff ? 'cursor-default' : 'cursor-pointer'}`}
                      data-testid={`merge-source-${field}`}
                    >
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">
                        {FIELD_LABELS[field] || field}
                      </span>
                      <span className={`${sv ? 'text-slate-900' : 'text-slate-300 italic'} ${sel === 'source' ? 'font-medium' : ''}`}>
                        {sv || 'Empty'}
                      </span>
                      {sel === 'source' && isDiff && (
                        <Check className="w-3.5 h-3.5 text-blue-600 inline ml-2" />
                      )}
                    </button>

                    {/* Divider */}
                    <div className={`w-px ${isDiff ? 'bg-amber-200' : 'bg-slate-100'}`} />

                    {/* Target value */}
                    <button
                      onClick={() => isDiff && toggle(field)}
                      disabled={!isDiff}
                      className={`text-left px-3 py-2.5 text-sm transition-colors ${
                        sel === 'target'
                          ? 'bg-blue-50 border-r-2 border-r-blue-500'
                          : 'hover:bg-slate-50 border-r-2 border-r-transparent'
                      } ${!isDiff ? 'cursor-default' : 'cursor-pointer'}`}
                      data-testid={`merge-target-${field}`}
                    >
                      <span className="text-[10px] uppercase tracking-wider text-slate-400 block mb-0.5">
                        {FIELD_LABELS[field] || field}
                      </span>
                      <span className={`${tv ? 'text-slate-900' : 'text-slate-300 italic'} ${sel === 'target' ? 'font-medium' : ''}`}>
                        {tv || 'Empty'}
                      </span>
                      {sel === 'target' && isDiff && (
                        <Check className="w-3.5 h-3.5 text-blue-600 inline ml-2" />
                      )}
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
              {(() => {
                const sType = preview.source.source_type;
                const tType = preview.target.source_type;
                if (sType === 'customer' && tType === 'customer') {
                  return `The losing customer record will be archived (not deleted).`;
                }
                if (tType === 'customer') {
                  return `The customer record will be updated with the values selected above, and the ${SOURCE_LABELS[sType].toLowerCase()} record will be permanently deleted.`;
                }
                return `The ${SOURCE_LABELS[sType].toLowerCase()} record will be permanently deleted after the merge.`;
              })()}
            </div>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="merge-cancel-btn">Cancel</Button>
          <Button
            onClick={handleMerge}
            disabled={merging || loading}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="merge-confirm-btn"
          >
            {merging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <GitMerge className="w-4 h-4 mr-2" />}
            Merge Records
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
